import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { AuthService } from 'src/app/core/services/authentication.service';
import { Global } from 'src/app/core/resources/global';
import { ManualCreationCargoService } from 'src/app/modules/cargo/manual-creation-cargo/manual-creation-cargo.service';
import { Router } from '@angular/router';
import { VehicleCargo } from 'src/app/core/interfaces/vehicle-cargo';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { FormControl, Validators, AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { Fmt } from 'src/app/core/messages/fmt';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Utils } from 'src/app/core/resources/utils';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { VehicleAlias } from 'src/app/core/interfaces/vehicleAlias';
import { vehicleAxles } from 'src/app/core/resources/vehicleAxles';

@Component({
  selector: 'app-type-vehicle',
  templateUrl: './type-vehicle.component.html',
  styleUrls: ['./type-vehicle.component.scss'],
  providers: [AuthService, Global]
})
export class TypeVehicleComponent implements OnInit {
  listVehicles: Observable<VehicleCargo[]>;
  vehicleTypes: VehicleCargo[] = [];
  @Input() inputFormControl: FormControl;
  vehicleCtrl: FormControl = new FormControl();
  vehiclesSelected: FormArray = new FormArray([]);
  vehicleAliases: VehicleAlias[] = [];
  minAxles: number = 2;
  @Input() options: OptionsAutocomplete;
  @Input() validate: string = '';
  @Output() vehicleType = new EventEmitter<string>();
  constructor(
    public manualCreationCargoService: ManualCreationCargoService,
    public snackBarService: SnackBarService,
    private router: Router,
    public utils: Utils
  ) {
  }

  async ngOnInit() {
    await this.initVehicles();
    if (this.isManualMode) {
      await this.getVehicleAliases();
      this.setManualInitialValues();
    }
    const validator = this.inputFormControl && this.inputFormControl.validator ? this.inputFormControl.validator({} as AbstractControl) : '';
    if (validator && validator.required) this.vehicleCtrl.setValidators(Validators.required);
    this.vehicleCtrl.updateValueAndValidity({ emitEvent: false });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes.options && changes.options.currentValue) {
        if (changes.options.currentValue.initialValue || changes.options.currentValue.initialValue === "") {
          if (changes.options.currentValue.initialValue === "") this.vehicleCtrl.setValue('');
          else {
            if (this.inputFormControl) this.vehicleCtrl.setValue({ name: changes.options.currentValue.initialValue })
          }
        }
      }
      if (changes.validate) {
        switch (this.validate) {
          case 'touched':
            this.vehicleCtrl.markAsTouched();
            break;
          case 'untouched':
            this.vehicleCtrl.markAsUntouched();
            break;
          case 'enable':
            this.vehicleCtrl.enable();
            break;
          case 'disable':
            this.vehicleCtrl.disable();
            break;
          case 'disable&untouched':
            this.vehicleCtrl.markAsUntouched();
            this.vehicleCtrl.disable();
            break;
          case 'setValidators':
            this.vehicleCtrl.setValidators(Validators.required);
            this.vehicleCtrl.updateValueAndValidity();
            break;
          case 'clearValidators':
            this.vehicleCtrl.clearValidators();
            this.vehicleCtrl.updateValueAndValidity();
            break;
          default:
            break;
        }
      }
    }

  }

  changeValue() {
    if (this.inputFormControl) this.inputFormControl.setValue('');
  }

  async initVehicles() {
    await this.getVehicleType();
    this.initAutocomplete();
  }

  private async getVehicleAliases() {
    try {
      const aliases = await this.manualCreationCargoService.getVehicleAliases().toPromise();
      this.vehicleAliases = aliases && aliases.length ? aliases.sort((a, b) => a.name.localeCompare(b.name)) : [];
    } catch (error) {
      this.vehicleAliases = [];
    }
  }

  private setManualInitialValues(): void {
    this.manualCreationCargoService.vehiclesSelected.forEach(vehicle => {
      this.vehiclesSelected.push(new FormGroup({
        vehicle: new FormControl(vehicle.name),
        vehicleAlias: new FormControl(vehicle.vehicleAlias),
        vehicleAxles: new FormControl(vehicle.axles, [Validators.required, Validators.min(this.minAxles)]),
      }));
    });
  }

  async getVehicleType(): Promise<void> {
    try {
      const data = await this.manualCreationCargoService.getVehicleType().toPromise();
      this.vehicleTypes = data && data.catalog && data.catalog.length > 0
        ? data.catalog.filter(vehicle => vehicle.id !== '24' && vehicle.id !== '41')
        : this.manualCreationCargoService.listVehicles.filter(vehicle => vehicle.id !== '24' && vehicle.id !== '41');
      this.vehicleTypes = this.vehicleTypes.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error("Error obteniendo tipos de vehículos:", error);
      this.vehicleTypes = this.manualCreationCargoService.listVehicles.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  get vehicleTypesAndAliases(): (VehicleCargo | VehicleAlias)[] {
    return [...this.vehicleTypes, ...this.vehicleAliases].sort((a, b) => a.name.localeCompare(b.name));
  }

  onSelectVehicleType($event: MatSelectChange): void {
    const vehicle = $event.value as VehicleCargo | VehicleAlias;
    if (!vehicle || !vehicle.name) return;
    const vehicleName = vehicle['vehicleType'] ? vehicle['vehicleType'].name : vehicle.name;
    const vehicleAlias = vehicle['vehicleType'] ? vehicle.name : '';
    const axles = vehicle.axles ? vehicle.axles : vehicleAxles[vehicleName] ? vehicleAxles[vehicleName] : null;
    this.vehiclesSelected.push(new FormGroup({
      vehicle: new FormControl(vehicleName),
      vehicleAlias: new FormControl(vehicleAlias),
      vehicleAxles: new FormControl(axles, [Validators.required, Validators.min(this.minAxles)]),
    }));
    this.manualCreationCargoService.vehiclesSelected.push({
      id: vehicle.id ? vehicle.id : null,
      name: vehicleName,
      quantity: 1,
      axles: axles,
      vehicleAlias: vehicleAlias,
      quality: '',
      trailer: { bodywork: '', axles: null },
      bodyWorkType: { id: null, name: null }
    });
    this.vehicleCtrl.setValue(null);
  }

  onChangeVehicleAxles(event: Event, index: number): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.vehiclesSelected.at(index).get('vehicleAxles').setValue(value);
    this.manualCreationCargoService.vehiclesSelected[index].axles = value;
  }

  isAliasSelected(index: number): boolean {
    const control = this.vehiclesSelected.at(index);
    return !!control && !!control.get('vehicleAlias') && !!control.get('vehicleAlias').value;
  }

  removeSelectedVehicle(index: number): void {
    this.vehiclesSelected.removeAt(index);
    this.manualCreationCargoService.vehiclesSelected.splice(index, 1);
    this.manualCreationCargoService.vehiclesSelectedWithLicensePlate = [];
  }

  showAxlesControl(vehicleName: string): boolean {
    return vehicleName && !vehicleAxles[vehicleName];
  }

  nextStep() {
    this.vehiclesSelected.markAllAsTouched();
    if (!this.manualCreationCargoService.listVehicles.length)
      return this.snackBarService.openSnackBar(Fmt.string(FormMessages.UNSELECTED_FIELD, 'el tipo de vehículo'), undefined, 'alert');
    for (let i = 0; i < this.vehiclesSelected.length; i++) {
      if (this.utils.errorMessagesCustomized(this.vehiclesSelected.at(i).get('vehicleAxles'), `número de ejes del vehículo ${i + 1}`, null, null, this.minAxles)) return;
    }
    this.manualCreationCargoService.getComponentActive().setId(2);
    this.manualCreationCargoService.getComponentActive().getStep().setId(2);
    this.router.navigate([this.manualCreationCargoService.steps.quantityVehicles.url]);
  }

  private initAutocomplete(): void {
    if (this.vehicleCtrl) {
      this.vehicleCtrl.valueChanges.pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((text: string | VehicleCargo) => {
          if (!text || typeof text !== 'string' || text.length <= 2) {
            return of(this.vehicleTypes);
          }
          const filteredVehicles = this.vehicleTypes.filter(vehicle => {
            if (typeof vehicle === 'object' && vehicle !== null && 'name' in vehicle && typeof text === 'string') {
              return vehicle.name.toLowerCase().includes(text.toLowerCase());
            }
            return false;
          });
          this.vehicleTypes = filteredVehicles;
          return of(this.vehicleTypes);
        })
      ).subscribe((filteredVehicles: VehicleCargo[]) => {
        this.vehicleTypes = filteredVehicles;
      });
    }
  }

  getNameIconVehicle(vehicleName: string) {
    return '/assets/svg/icons/icon-' + vehicleName.toLowerCase() + '.svg';
  }

  onErrorRenderImage($event: Event) {
    ($event.target as HTMLImageElement).src = '/assets/svg/icons/icon-camion.svg';
  }

  displayVehicleType(vehicle): string | undefined {
    return vehicle ? vehicle.name : undefined;
  }

  onSelectValue($event: MatAutocompleteSelectedEvent) {
    if (this.inputFormControl) this.inputFormControl.setValue($event.option.value)
  }

  stepBack() {
    this.manualCreationCargoService.getComponentActive().setId(1);
    this.manualCreationCargoService.getComponentActive().getStep().setId(5);
    this.router.navigate([this.manualCreationCargoService.steps.typeService.url]);
  }

  private get isManualMode(): boolean {
    return !this.options || !this.options.showAutocomplete;
  }
}
